"use client"

import React, { useEffect } from "react"
import Link from "next/link"
import { loadProducts } from "@/actions/orderActions"
import { loadCardPricesForProductAction } from "@/actions/priceActions"
import { LoaderCircle } from "lucide-react"
import { useTranslations } from "next-intl"

import { generateProductLink } from "@/lib/link-utils/link-utils"
import { getSbLocaleFromIsoLocale } from "@/lib/locale-helper"
import { CardPrices } from "@/lib/price-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { ProductsResult } from "@/lib/storefront/account/order-service"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/components"
import { DataPagination } from "@/components/ui/components/data-pagination"
import { getResizedSbImagepath } from "@/components/ui/helpers/resize-sb-image"
import AddToBasketButtonClient from "@/components/product/AddToBasketButtonClient"
import { ProductCardDeliveryInfo } from "@/components/product/ProductCardDeliveryInfo"
import ProductCardDiscount from "@/components/product/ProductCardDiscount"
import ProductCardPrice from "@/components/product/ProductCardPrice"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

interface AccountOrderListClientsProps {
  accountContext: AccountContext
  initialProducts: ProductsResult
}

function ArticleListArticleCard(props: {
  article: ReducedProductData
  isoLocale: string
  isB2B: boolean
  customerId: string | undefined
}) {
  const [cardPrices, setCardPrices] = React.useState<CardPrices | undefined>(undefined)
  const t = useTranslations("storefront")

  const variantLink = generateProductLink(
    props.article.title,
    props.article.id,
    undefined,
    getSbLocaleFromIsoLocale(props.isoLocale)
  )
  useEffect(() => {
    loadCardPricesForProductAction(
      props.isoLocale,
      props.isB2B,
      props.article?.ecoTax,
      props.article.id,
      props.customerId
    ).then((loadedCardPrices) => {
      if (loadedCardPrices) {
        //console.dir(loadedCardPrices)
        setCardPrices(loadedCardPrices)
      }
    })
  }, [props.article, props.customerId, props.isB2B, props.isoLocale])

  return (
    <div className={"relative flex w-full flex-col gap-2"}>
      <ProductCardDiscount
        variant={"landscape"}
        isoLocale={props.isoLocale}
        isB2BCustomer={props.isB2B}
        product={props.article}
        prices={undefined}
        _cardPrices={cardPrices}
      />

      {props.article.image ? (
        <ImageWithFallback
          unoptimized={true}
          className={cn(
            "aspect-square max-h-[270px] overflow-hidden object-contain transition duration-200 ease-in-out group-hover:scale-105"
          )}
          alt={props.article.title}
          src={getResizedSbImagepath(props.article.image)}
          width={props.article.image_width}
          height={props.article.image_height}
        />
      ) : (
        <div className={"aspect-square max-h-[270px] w-full "} />
      )}
      <div className={"flex w-full flex-col justify-between gap-2"}>
        <div className="flex min-h-24 grow flex-col justify-end gap-2 md:min-h-24 lg:min-h-28">
          {/*title*/}
          <div className={cn("h-8 max-h-8 w-full text-left")}>
            <p
              className={cn(
                "line-clamp-2 overflow-hidden text-ellipsis text-sm font-bold !leading-tight text-gray-900 sm:text-base"
              )}
            >
              {props.article.title}
            </p>
          </div>
          {/*description*/}

          <p
            className={cn(
              "line-clamp-1 h-6 max-h-6 overflow-hidden text-ellipsis text-sm @md/productcard:h-8 @md/productcard:min-h-8"
            )}
            dangerouslySetInnerHTML={{
              __html: props.article.description ? props.article.description : "",
            }}
          />
          {/*prices and buttons*/}
          <div className={"flex h-full flex-col justify-between gap-4"}>
            <div className={"min-h-22 flex flex-row gap-1"}>
              <div className={"flex min-h-20 w-full grow flex-col justify-end gap-1"}>
                <ProductCardDeliveryInfo isoLocale={props.isoLocale} productData={props.article} />
              </div>

              {/*truck and multicolor*/}
            </div>
          </div>
        </div>

        <div className={"flex flex-col gap-2"}>
          {cardPrices ? (
            <ProductCardPrice
              productData={props.article}
              cardPrices={cardPrices}
              showEcoTaxFlag={true}
              isB2BCustomer={props.isB2B}
              showPrice={true}
              isoLocale={props.isoLocale}
            />
          ) : (
            <LoaderCircle className={"animation-spin size-6"} />
          )}
        </div>
        <div className={"flex flex-row gap-2"}>
          <Link prefetch={false} href={variantLink}>
            <Button size={"productCard"} className="w-full" variant={"default"}>
              {t("product.go_to_product")}
            </Button>
          </Link>

          <AddToBasketButtonClient
            isoLocale={props.isoLocale}
            isB2BCustomer={props.isB2B}
            articleIdToAdd={props.article.id}
            productData={props.article}
            prices={props.article.effectivePrices}
            quantity={props.article?.stepOrderQuantity ?? 1}
            simulateProductClick={true}
          />
        </div>
      </div>
    </div>
  )
}

const SORT_BY_DATE_ASC = "SORT_BY_DATE_ASC"

export default function AccountArticleListClient(props: AccountOrderListClientsProps) {
  const t = useTranslations("storefront")

  const [articles, setArticles] = React.useState<ProductsResult>(props.initialProducts)

  async function getPagedProducts(page: number) {
    setArticles(
      await loadProducts({
        pageSize: 10,
        pageNumber: page,
      })
    )
  }

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        <div className={"flex flex-row items-start justify-between gap-4"}>
          <div className={"flex w-full flex-col gap-4"}>
            <p>Hier finden Sie Ihre bestellten Artikel.</p>
          </div>
        </div>

        <div className={"grid grid-cols-1 gap-2 lg:grid-cols-3"}>
          {articles?.products?.map((article) => (
            <div
              key={article.id}
              className={"flex min-h-96 w-full flex-col gap-1 rounded-sm border border-gray-200 p-2"}
            >
              <ArticleListArticleCard
                article={article}
                isoLocale={props.accountContext.isoLocale}
                isB2B={props.accountContext.isB2B}
                customerId={props.accountContext?.customerData?.customerNo}
              />
            </div>
          ))}
        </div>
        <DataPagination
          initialPage={1}
          totalPages={Math.ceil(articles?.totalSize / 10)}
          onPageChange={(page) => getPagedProducts(page)}
        ></DataPagination>
      </div>
    </>
  )
}
